import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LEVEL_DEG,
  PLACEMENT_GROUP_LABELS,
  solve,
  type LevelingResult,
} from '@camper-diary/shared';
import {
  loadLevelerConfig,
  submitLevelerMeasure,
  type LevelerConfig,
} from '../api/leveler';
import { useTilt } from '../hooks/useTilt';
import { formatDeg, toCamper } from '../utils/tilt';
import { LevelVial } from '../components/LevelVial';
import { WedgeAdvice } from '../components/WedgeAdvice';

type Banner = { kind: 'good' | 'warn' | 'bad'; text: string } | null;

/** Firma del consiglio: serve solo a non far ridisegnare il verdetto quando il
 *  calcolo, a telefono fermo, continua a dare lo stesso risultato. */
const signature = (r: LevelingResult) =>
  [r.level, r.group, ...r.placements.map((p) => `${p.wheel}:${p.step}:${p.wedge}`)].join('|');

export function LevelerPage() {
  const [config, setConfig] = useState<LevelerConfig | null>(null);
  const [banner, setBanner] = useState<Banner>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Manca qualcosa da configurare: anche la sola calibrazione, che non impedisce
  // di leggere ma rende i numeri inattendibili, deve portare a Impostazioni.
  const [incomplete, setIncomplete] = useState(false);

  const { reading, status, start } = useTilt();

  const [advice, setAdvice] = useState<LevelingResult | null>(null);
  // Il consiglio congelato nel momento in cui l'utente è salito sui cunei: la
  // verifica confronta quello, non quello che la livella direbbe adesso.
  const [frozen, setFrozen] = useState<LevelingResult | null>(null);
  const [measureNote, setMeasureNote] = useState<Banner>(null);
  const [learning, setLearning] = useState(false);
  const [learned, setLearned] = useState(false);

  const adviceSig = useRef<string | null>(null);

  /* ---- caricamento ------------------------------------------------------ */

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { config: loaded, offline } = await loadLevelerConfig();
        if (!alive) return;
        setConfig(loaded);

        const missing: string[] = [];
        if (!loaded.vehicle) missing.push('le misure del camper');
        if (!loaded.wedges) missing.push('i gradini dei cunei');
        if (!loaded.calibration) missing.push('la calibrazione del punto zero');

        if (missing.length > 0) {
          setIncomplete(true);
          setBanner({ kind: 'warn', text: `Prima di livellare mancano: ${missing.join(', ')}.` });
        } else if (offline) {
          setBanner({
            kind: 'warn',
            text: 'Sei senza rete: uso l\'ultima configurazione salvata. Il calcolo funziona lo stesso.',
          });
        }
      } catch (e) {
        if (!alive) return;
        setLoadFailed(true);
        setBanner({
          kind: 'bad',
          text: `Configurazione non disponibile: ${e instanceof Error ? e.message : 'errore'}`,
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ---- lettura ---------------------------------------------------------- */

  const camper = useMemo(() => {
    if (!reading || !config?.vehicle) return null;
    return toCamper(reading, config.calibration, config.vehicle.phoneFacing);
  }, [reading, config]);

  // Il consiglio si aggiorna solo a telefono fermo: un numero che cambia mentre
  // qualcuno si muove dentro il camper non è un consiglio, è rumore.
  useEffect(() => {
    if (!camper?.steady || frozen) return;
    if (!config?.vehicle || !config.wedges) return;

    const next = solve({
      roll: camper.roll,
      pitch: camper.pitch,
      track: config.vehicle.trackCm,
      wheelbase: config.vehicle.wheelbaseCm,
      wedgeA: config.wedges.wedgeA,
      wedgeB: config.wedges.wedgeB,
      gainRoll: config.tuning.gainRoll,
      gainPitch: config.tuning.gainPitch,
    });

    const sig = signature(next);
    if (sig === adviceSig.current) return;
    adviceSig.current = sig;
    setAdvice(next);
  }, [camper, config, frozen]);

  /* ---- avvio dei sensori ------------------------------------------------ */

  const canStart = Boolean(config?.vehicle && config?.wedges);

  async function onStart() {
    await start();
  }

  useEffect(() => {
    if (status === 'insecure') {
      setBanner({
        kind: 'bad',
        text: 'Questa pagina non è su HTTPS: il browser blocca i sensori.',
      });
    } else if (status === 'unsupported') {
      setBanner({
        kind: 'bad',
        text: 'Questo dispositivo non ha sensori di movimento. Serve un telefono.',
      });
    } else if (status === 'denied') {
      setBanner({
        kind: 'bad',
        text: 'Permesso negato. Su iPhone: Impostazioni → Safari → Movimento e orientamento, poi ricarica.',
      });
    }
  }, [status]);

  /* ---- verifica --------------------------------------------------------- */

  function onClimbed() {
    if (!advice) return;
    setFrozen(advice);
    setMeasureNote(null);
    setLearned(false);
  }

  function onBack() {
    setFrozen(null);
    setMeasureNote(null);
    adviceSig.current = null;
  }

  async function onLearn() {
    if (!frozen || !camper) return;
    setLearning(true);
    try {
      const res = await submitLevelerMeasure({
        rollBefore: frozen.before.roll,
        rollAfter: camper.roll,
        rollPredictedDelta: frozen.predicted.rollDelta,
        pitchBefore: frozen.before.pitch,
        pitchAfter: camper.pitch,
        pitchPredictedDelta: frozen.predicted.pitchDelta,
      });

      setConfig((c) => (c ? { ...c, tuning: res.tuning } : c));

      const lines = (['roll', 'pitch'] as const).map((axis) => {
        const a = res.axes[axis];
        const name = axis === 'roll' ? 'Rollio' : 'Beccheggio';
        return a.applied
          ? `${name}: fattore da ${a.from.toFixed(2)} a ${a.to.toFixed(2)}.`
          : `${name}: non usato, ${a.reason}.`;
      });

      setMeasureNote({
        kind: 'good',
        text: `${lines.join(' ')} Basato su ${res.tuning.samples} misure.`,
      });
      setLearned(true);
    } catch (e) {
      setMeasureNote({
        kind: 'bad',
        text: `Misura non registrata: ${e instanceof Error ? e.message : 'errore'}`,
      });
    } finally {
      setLearning(false);
    }
  }

  /* ---- render ----------------------------------------------------------- */

  const running = status === 'running';
  const stepsFor = (wedge: 'A' | 'B') =>
    wedge === 'A' ? (config?.wedges?.wedgeA ?? []) : (config?.wedges?.wedgeB ?? []);

  return (
    <div className="page leveler">
      <div className="page-head">
        <div className="title-block">
          <h1>Livella</h1>
          <p className="muted">
            Quale gradino mettere sotto ogni ruota per raddrizzare il camper.
          </p>
        </div>
      </div>

      {banner && <div className={`note ${banner.kind}`}>{banner.text}</div>}

      {!running && (
        <section className="card leveler-gate">
          <span className="tag">Per cominciare</span>
          <h2>Attiva i sensori</h2>
          <p>
            Appoggia il telefono nel punto che hai calibrato, con lo schermo in su, e tocca
            il pulsante. Il browser chiede il permesso una volta sola.
          </p>
          <div className="btn-row">
            <button
              className="btn btn-primary"
              onClick={() => void onStart()}
              disabled={!canStart || loadFailed}
            >
              Attiva i sensori
            </button>
          </div>
          {incomplete && !loadFailed && (
            <p className="muted">
              Misure, cunei e punto zero si configurano in{' '}
              <Link to="/settings/livella">Impostazioni → Livella</Link>.
            </p>
          )}
        </section>
      )}

      {running && camper && !frozen && (
        <>
          <LevelVial roll={camper.roll} pitch={camper.pitch} />

          <div className="angles">
            <div className="angle">
              <span className="tag">Rollio</span>
              <span
                className={
                  Math.abs(camper.roll) <= LEVEL_DEG ? 'value centred' : 'value'
                }
              >
                {formatDeg(camper.roll)}
              </span>
              <span className="side">
                {Math.abs(camper.roll) <= LEVEL_DEG
                  ? 'in piano'
                  : camper.roll > 0
                    ? 'sinistra più alta'
                    : 'destra più alta'}
              </span>
            </div>
            <div className="angle">
              <span className="tag">Beccheggio</span>
              <span
                className={
                  Math.abs(camper.pitch) <= LEVEL_DEG ? 'value centred' : 'value'
                }
              >
                {formatDeg(camper.pitch)}
              </span>
              <span className="side">
                {Math.abs(camper.pitch) <= LEVEL_DEG
                  ? 'in piano'
                  : camper.pitch > 0
                    ? 'muso più alto'
                    : 'coda più alta'}
              </span>
            </div>
          </div>

          <p className="steady" data-steady={String(camper.steady)}>
            {camper.steady ? 'Lettura stabile' : 'Telefono in movimento — aspetta che si fermi'}
          </p>

          {advice?.level && (
            <div className="note good">Il camper è già in piano. Nessun cuneo da mettere.</div>
          )}

          {advice && !advice.level && (
            <section className="verdict">
              <div className="verdict-head">
                <h2>{PLACEMENT_GROUP_LABELS[advice.group]}</h2>
                <span className="tag">
                  residuo previsto {formatDeg(advice.after.roll)} / {formatDeg(advice.after.pitch)}
                </span>
              </div>

              <div className="wheels">
                {advice.placements.map((p) => (
                  <WedgeAdvice key={p.wheel} placement={p} steps={stepsFor(p.wedge)} />
                ))}
              </div>

              {advice.warnings.map((w) => (
                <div
                  key={w.kind}
                  className={`note ${w.kind === 'out-of-range' ? 'bad' : 'warn'}`}
                >
                  {w.text}
                </div>
              ))}

              <div className="btn-row">
                <button className="btn btn-primary" onClick={onClimbed}>
                  Sono salito sui cunei
                </button>
              </div>
            </section>
          )}
        </>
      )}

      {frozen && (
        <section className="card">
          <span className="tag">Verifica</span>
          <h2>Com'è andata</h2>
          <p>
            Confronto fra quello che la livella aveva promesso e quello che il camper ha
            fatto davvero.
          </p>

          <div className="compare">
            <span className="who">Rollio previsto</span>
            <span className="arrow">→</span>
            <span className="num">{formatDeg(frozen.after.roll)}</span>
          </div>
          <div className="compare">
            <span className="who">Rollio misurato</span>
            <span className="arrow">→</span>
            <span className="num">{camper ? formatDeg(camper.roll) : '—'}</span>
          </div>
          <div className="compare">
            <span className="who">Beccheggio previsto</span>
            <span className="arrow">→</span>
            <span className="num">{formatDeg(frozen.after.pitch)}</span>
          </div>
          <div className="compare">
            <span className="who">Beccheggio misurato</span>
            <span className="arrow">→</span>
            <span className="num">{camper ? formatDeg(camper.pitch) : '—'}</span>
          </div>

          {measureNote && <div className={`note ${measureNote.kind}`}>{measureNote.text}</div>}

          <div className="btn-row">
            <button
              className="btn btn-primary"
              onClick={() => void onLearn()}
              disabled={learning || learned || !camper}
            >
              {learned ? 'Misura registrata' : 'Usa questa misura per tarare'}
            </button>
            <button className="btn" onClick={onBack}>
              Torna al livellamento
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
